import React, { useEffect, useMemo, useState } from 'react';
import styles from './Email.module.css';

const Toggle = ({ id, checked, disabled, onChange, label }) => (
  <label className={styles.toggle} htmlFor={id}>
    <input
      id={id}
      type="checkbox"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
    />
    <span className={styles.slider} />
  </label>
);

/**
 * Category toggles shared by the public /email-preferences page and the Profile overlay.
 * Changes are kept as a local draft until Save is pressed.
 *
 * props:
 *  - categoryMeta: [{ key, label, description, locked }] from the API
 *  - categories:   { [optionalKey]: boolean } current saved state
 *  - canIncrease:  false when the link is too old to turn emails back on
 *  - onSave(nextCategories) => Promise<{ ok, error? }>
 *  - dark:         render the dark variant (Profile in dark theme)
 */
const PreferencesPanel = ({ categoryMeta = [], categories = {}, canIncrease = true, onSave, dark = false, idPrefix = 'email-pref' }) => {
  const optional = useMemo(() => categoryMeta.filter((c) => !c.locked), [categoryMeta]);
  const locked = useMemo(() => categoryMeta.filter((c) => c.locked), [categoryMeta]);

  const [draft, setDraft] = useState(categories);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => { setDraft(categories); }, [categories]);

  const anyOn = optional.some((c) => draft[c.key]);
  const dirty = optional.some((c) => Boolean(draft[c.key]) !== Boolean(categories[c.key]));
  // A stale link may only switch things off; anything currently off stays locked.
  const cannotEnable = (key) => !canIncrease && !categories[key];

  const setOne = (key, value) => {
    setMessage(null);
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const setAll = (value) => {
    setMessage(null);
    setDraft((d) => {
      const next = { ...d };
      optional.forEach((c) => {
        if (value && cannotEnable(c.key)) return;
        next[c.key] = value;
      });
      return next;
    });
  };

  const handleSave = async () => {
    if (!onSave || !dirty) return;
    setSaving(true);
    setMessage(null);
    const result = await onSave(draft);
    setSaving(false);
    if (result && result.ok) setMessage({ type: 'success', text: 'Your email preferences have been saved.' });
    else setMessage({ type: 'error', text: (result && result.error) || 'Could not save your preferences. Please try again.' });
  };

  const masterDisabled = saving || (!anyOn && optional.every((c) => cannotEnable(c.key)));

  return (
    <div className={`${styles.panel} ${dark ? styles.dark : ''}`}>
      <div className={styles.masterRow}>
        <div className={styles.rowText}>
          <div className={styles.rowLabel}>Receive optional emails</div>
          <div className={styles.rowDesc}>
            {anyOn ? 'Turn off to unsubscribe from all optional emails at once.' : 'You are unsubscribed from all optional emails.'}
          </div>
        </div>
        <Toggle id={`${idPrefix}-all`} label="Receive optional emails" checked={anyOn} disabled={masterDisabled} onChange={setAll} />
      </div>

      <div className={styles.sectionLabel}>Choose what you receive</div>
      {optional.map((c) => (
        <div className={styles.row} key={c.key}>
          <div className={styles.rowText}>
            <div className={styles.rowLabel}>{c.label}</div>
            <div className={styles.rowDesc}>{c.description}</div>
          </div>
          <Toggle
            id={`${idPrefix}-${c.key}`}
            label={c.label}
            checked={Boolean(draft[c.key])}
            disabled={saving || cannotEnable(c.key)}
            onChange={(v) => setOne(c.key, v)}
          />
        </div>
      ))}

      {locked.length > 0 && (
        <>
          <div className={styles.sectionLabel}>Always sent</div>
          {locked.map((c) => (
            <div className={styles.row} key={c.key}>
              <div className={styles.rowText}>
                <div className={styles.rowLabel}>
                  {c.label}
                  <span className={styles.lockedTag}>Required</span>
                </div>
                <div className={styles.rowDesc}>{c.description}</div>
              </div>
              <Toggle id={`${idPrefix}-${c.key}`} label={`${c.label} (required)`} checked disabled onChange={() => {}} />
            </div>
          ))}
        </>
      )}

      {!canIncrease && (
        <p className={styles.note}>
          For your security, this link can only turn emails off. To turn emails back on, request a new link or sign in to your account.
        </p>
      )}

      {message && (
        <p className={message.type === 'success' ? styles.success : styles.error} role={message.type === 'error' ? 'alert' : 'status'}>
          {message.text}
        </p>
      )}

      <div className={styles.panelActions}>
        {dirty && (
          <button type="button" className={styles.secondaryBtn} disabled={saving} onClick={() => { setDraft(categories); setMessage(null); }}>
            Cancel
          </button>
        )}
        <button type="button" className={styles.primaryBtn} disabled={saving || !dirty} onClick={handleSave}>
          {saving ? 'Saving…' : 'Save preferences'}
        </button>
      </div>
    </div>
  );
};

export default PreferencesPanel;
